import { useState } from "react";
import { CalendarClock, Check, Copy, Gift, Link2Off, MessageCircle, Smartphone, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const fmtUnlock = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
const digits = (s) => (s || "").replace(/\D/g, "");
const toE164 = (s) => { const d = digits(s); return d.length === 10 ? `+91${d}` : d.length > 10 && d.length <= 15 ? `+${d}` : ""; };

export default function GiftPanel({ c, onChange }) {
  const { user } = useAuth();
  const [ded, setDed] = useState(c.dedication || "");
  const [phone, setPhone] = useState(c.recipient_phone || "");
  const [scheduled, setScheduled] = useState(!!c.unlock_at);
  const [when, setWhen] = useState(toLocalInput(c.unlock_at));
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = c.gift_token ? `${window.location.origin}/g/${c.gift_token}` : "";
  const unlockIso = scheduled && when ? new Date(when).toISOString() : null;
  const dirty = ded.trim() !== (c.dedication || "") || (scheduled ? when : "") !== toLocalInput(c.unlock_at) || (toE164(phone) || "") !== (c.recipient_phone || "");
  const myPhone = user?.phone || "";

  const save = async () => {
    if (scheduled && !when) { toast.error("Pick the date and time to unlock"); return null; }
    if (unlockIso && new Date(unlockIso) <= new Date()) { toast.error("Unlock time must be in the future"); return null; }
    if (phone.trim() && !toE164(phone)) { toast.error("Enter a valid mobile number — 10 digits, or with country code"); return null; }
    setBusy(true);
    try { const r = await api.patch(`/caricatures/${c.id}/gift`, { dedication: ded, unlock_at: unlockIso, recipient_phone: phone }); onChange({ ...c, ...r.data }); return r.data; }
    catch (e) { toast.error(errMsg(e)); return null; }
    finally { setBusy(false); }
  };
  const ensure = async () => (!c.gift_token || dirty) ? save() : { gift_token: c.gift_token, dedication: c.dedication, unlock_at: c.unlock_at, recipient_phone: c.recipient_phone };
  const whatsapp = async () => {
    if (!phone.trim()) { toast.error("Whose WhatsApp should it go to? Add their number (or tap “Send to myself”)."); return; }
    const g = await ensure();
    if (!g) return;
    const link = `${window.location.origin}/g/${g.gift_token}`;
    const lockLine = g.unlock_at ? `It unlocks on ${fmtUnlock(g.unlock_at)} — save the link!\n\n` : "";
    const text = (g.dedication ? `"${g.dedication}"\n\n` : "") + `A caricature of ${c.name}, drawn just for you. ${lockLine}Open it here: ${link}`;
    let delivered = false;
    try { const r = await api.post(`/caricatures/${c.id}/gift/send`); delivered = r.data.delivered; } catch { delivered = false; }
    if (delivered) { toast.success(`Sent to ${g.recipient_phone} on WhatsApp`); return; }
    window.open(`https://wa.me/${digits(g.recipient_phone)}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const copy = async () => {
    const g = await ensure();
    if (!g) return;
    try { await navigator.clipboard.writeText(`${window.location.origin}/g/${g.gift_token}`); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy — long-press the link instead"); }
  };
  const revoke = async () => {
    try { await api.delete(`/caricatures/${c.id}/gift`); onChange({ ...c, gift_token: null }); toast("Gift link turned off"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-6 border-t border-[#E8E2D9] pt-6" data-testid="gift-panel">
      <p className="flex items-center gap-2 font-semibold"><Gift className="h-4 w-4 text-[#3B5BDB]" />Send it on WhatsApp</p>
      <p className="mt-1 text-xs text-[#8C8498]">To yourself or to someone else — we'll open a chat with that number and drop the poster link in.</p>
      <div className="mt-3 flex gap-2">
        <div className="relative flex-1">
          <Smartphone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8C8498]" />
          <Input type="tel" inputMode="tel" autoComplete="tel" data-testid="gift-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="Recipient's WhatsApp number" className="h-11 bg-white pl-9" />
        </div>
        {myPhone && (
          <button type="button" data-testid="gift-send-to-me-btn" onClick={() => setPhone(myPhone)} className="btn-ghost !px-3 !text-xs" title={`Use my number ${myPhone}`}>
            <UserRound className="h-3.5 w-3.5" />Send to myself
          </button>
        )}
      </div>
      {!myPhone && <p className="mt-1 text-[11px] text-[#8C8498]">Sending it to yourself? Just enter your own number.</p>}

      <p className="mt-4 text-xs text-[#8C8498]">Add a dedication — it's printed under the artwork on the downloaded poster and shown on the gift page.</p>
      <Input data-testid="gift-dedication-input" value={ded} maxLength={140} onChange={(e) => setDed(e.target.value)}
        placeholder={`e.g. Happy 30th, ${c.name}! — from the gang`} className="mt-2 h-11 bg-white" />
      <p className="mt-1 text-right font-mono text-[10px] text-[#8C8498]" data-testid="gift-dedication-count">{ded.length}/140</p>

      <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm" data-testid="gift-schedule-toggle-label">
        <input type="checkbox" data-testid="gift-schedule-toggle" checked={scheduled} onChange={(e) => setScheduled(e.target.checked)} className="h-4 w-4 accent-[#3B5BDB]" />
        <CalendarClock className="h-4 w-4 text-[#8C8498]" />Unlock on a date & time
      </label>
      {scheduled && (
        <div className="mt-2 rounded-lg bg-[#E9EEFF]/70 p-3" data-testid="gift-schedule-box">
          <Input type="datetime-local" data-testid="gift-unlock-input" value={when} min={toLocalInput(new Date().toISOString())} onChange={(e) => setWhen(e.target.value)} className="h-11 bg-white" />
          <p className="mt-2 text-xs text-[#5E5868]">Send the link today — they'll see a countdown until {when ? fmtUnlock(new Date(when).toISOString()) : "the moment you pick"} (their local time).</p>
        </div>
      )}
      {c.unlock_at && !dirty && (
        <p className="mt-2 text-xs text-[#4F7A5C]" data-testid="gift-scheduled-note">Scheduled · unlocks {fmtUnlock(c.unlock_at)}</p>
      )}

      <button type="button" data-testid="gift-whatsapp-btn" onClick={whatsapp} disabled={busy}
        className="btn-primary mt-3 w-full !bg-[#25D366] !shadow-[0_6px_20px_-8px_rgba(37,211,102,0.7)] hover:!bg-[#1EBE5B]">
        <MessageCircle className="h-4 w-4" />{busy ? "Saving…" : toE164(phone) ? `Send on WhatsApp to ${toE164(phone)}` : "Send on WhatsApp"}
      </button>
      <div className="mt-3 flex gap-2">
        <button type="button" data-testid="gift-copy-btn" onClick={copy} disabled={busy} className="btn-ghost flex-1 !text-xs">
          {copied ? <Check className="h-3.5 w-3.5 text-[#789A82]" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copied" : dirty && c.gift_token ? "Save & copy link" : "Copy gift link"}
        </button>
        {c.gift_token && (
          <button type="button" data-testid="gift-revoke-btn" onClick={revoke} className="btn-ghost !text-xs !text-rose-700" aria-label="Turn off gift link"><Link2Off className="h-3.5 w-3.5" /></button>
        )}
      </div>
      {url && <p data-testid="gift-link-text" className="mt-2 truncate font-mono text-[10px] text-[#8C8498]">{url}</p>}
    </div>
  );
}
